<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import ArrowIcon from './icons/ArrowIcon.vue';

const props = defineProps<{
	value?: string;
	options: { id: string, label: string }[];
	styled?: boolean;
	selectOnClick?: boolean;
}>();

// Define emits using tuple syntax
const emit = defineEmits<{
	change: [id: string | undefined];
	openChange: [open: boolean];
}>();

const open = ref(false);
const dropdown = ref<HTMLElement | null>(null);

const clickable = computed(() => {
	return props.options.length > 1;
});

const label = computed(() => {
	const f = props.options.find((option) => option.id == props.value);
	return f ? f.label : "";
});

function clicked(event: MouseEvent) {
	if (!dropdown.value) return;
	const w = dropdown.value.offsetWidth;
	if ((props.selectOnClick && event.offsetX < w - 30) || !clickable.value) {
		emit('change', props.value);
	} else if (clickable.value) {
		open.value = !open.value;
	}
};

function optionClicked(option: { id: string, label: string }) {
	open.value = false;
	emit('change', option.id);
};

//click outside
function openClickHandler(event: MouseEvent) {
	if (!dropdown.value || !(dropdown.value as HTMLElement).contains(event.target as Node)) {
		open.value = false;
	}
};

watch(open, (isOpen) => {
	if (isOpen) {
		window.addEventListener("click", openClickHandler, true);
	} else {
		window.removeEventListener("click", openClickHandler, true);
	}
	emit('openChange', isOpen);
});
</script>



<template>
	<div class="dropdown" :class="{ clickable, styled, plain: !styled }" ref="dropdown" @click="clicked">
		<p>
			{{ label }}
		</p>
		<ArrowIcon v-if="clickable" class="icon" :direction="open ? 'up' : 'down'" />

		<div v-if="open" class="box styled-scrollbars">
			<a href="#" v-for="option in options" :key="option.id" @click.prevent.stop="optionClicked(option)"
				:class="{ 'selected': option.id == value }" class="ellipsis">
				{{ option.label }}
			</a>
		</div>
	</div>
</template>




<style scoped lang="scss">
.dropdown {
	position: relative;

	display: flex;
	flex-flow: row nowrap;
	align-items: center;
	justify-content: space-between;

	&.styled {
		p {
			margin: 0;
			font-weight: 500;
		}

		.icon {
			background-color: white;
			color: #333;
			box-shadow: 0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%);
			border-radius: 1rem;
			padding: 4px;
			width: 22px;
			height: 22px;
			pointer-events: none;
		}
	}

	&.plain {
		&.clickable p {
			border-right: solid 1px #bbb;
			margin-right: 6px;
			padding-right: 10px;
		}

		.icon {
			margin-left: 0px;
			margin-right: 0px;
			pointer-events: none;
		}
	}

	&.styled .box {
		width: calc(100% + .5rem);
		top: 1.8rem;
		left: -.5rem;
	}

	.box {
		position: absolute;
		min-width: 100%;
		// width: calc(100% + .5rem);
		top: 2.5rem;
		left: 0;
		// right: 0;
		background-color: white;
		z-index: 10000;
		padding: 5px 0;
		border-radius: 6px;
		// border: solid 1px #777;
		box-shadow: 0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%);
		overflow-y: auto;
		max-height: 80vh;

		a {
			white-space: nowrap;
			display: block;

			cursor: pointer;
			font-weight: normal;
			margin: 0;
			padding: .5rem 1rem;
			text-decoration: none;
		}

		a.selected {
			font-weight: bold;
		}

		a:hover {
			color: #777;
			background-color: #f1f1f1;
		}
	}

}

.clickable {
	cursor: pointer;
}
</style>